<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <button
            @click="goBack"
            class="back-button"
            :title="$t('login.backButtonTitle')"
          >
            <i class="el-icon-arrow-left"></i>
          </button>
          <img class="login-logo" :class="{ 'login-logo--mt': isMtBrand }" :src="$brand.logo" :alt="$t('brand.logoAlt')" />
          <h1 class="login-title">{{ $t("login.title") }}</h1>
          <p class="login-subtitle">{{ $t("login.subtitle") }}</p>
        </div>

        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="rules"
          label-position="top"
          class="login-form"
          @submit.native.prevent="handleLogin"
        >
          <el-form-item :label="$t('login.emailLabel')" prop="email">
            <el-input
              v-model="loginForm.email"
              :placeholder="$t('login.emailPlaceholder')"
              prefix-icon="el-icon-user"
            />
          </el-form-item>

          <el-form-item :label="$t('login.passwordLabel')" prop="password">
            <el-input
              v-model="loginForm.password"
              type="password"
              :placeholder="$t('login.passwordPlaceholder')"
              show-password
              prefix-icon="el-icon-lock"
            />
          </el-form-item>

          <el-form-item>
            <el-button
              type="primary"
              @click="handleLogin"
              class="login-btn"
              :loading="loading"
            >
              {{ $t("login.loginButton") }}
            </el-button>
          </el-form-item>

          <div class="form-options">
            <el-checkbox v-model="loginForm.rememberMe">{{
              $t("login.rememberMe")
            }}</el-checkbox>
            <el-link type="primary" :underline="false" @click="goResetPassword">{{
              $t("login.forgotPassword")
            }}</el-link>
          </div>

          <!-- 查看服务条款和隐私政策 -->
          <div class="terms-links">
            <button class="terms-link-btn" type="button" @click="showTerms">
              <i class="el-icon-document"></i>
              {{ $t("login.viewTerms") }}
            </button>
            <span class="terms-divider">|</span>
            <button class="terms-link-btn" type="button" @click="showTerms">
              <i class="el-icon-lock"></i>
              {{ $t("login.viewPrivacy") }}
            </button>
          </div>
        </el-form>

        <div class="register-prompt">
          <span>{{ $t("login.registerPrompt") }}</span>
          <el-link
            type="primary"
            :underline="false"
            @click="goRegister"
            class="register-link"
            >{{ $t("login.registerLink") }}</el-link
          >
        </div>
      </div>
    </div>

    <!-- 服务条款与隐私政策 - 统一弹窗 -->
    <el-dialog
      :title="$t('login.termsOfService') + ' / ' + $t('login.privacyPolicy')"
      :visible.sync="showTermsDialog"
      width="80%"
      top="0"
      :append-to-body="true"
      :modal-append-to-body="true"
      custom-class="terms-dialog-wrapper"
    >
      <div class="terms-content" ref="termsContent">
        <section class="terms-block">
          <h4 class="terms-block-title">{{ $t("login.termsOfService") }}</h4>
          <template v-if="Array.isArray(termsContentValue)">
            <div
              v-for="(section, i) in parsedSections"
              :key="`terms-${i}`"
              class="terms-section"
            >
              <h5 v-if="section.title" class="terms-title">{{ section.title }}</h5>
              <p
                v-for="(paragraph, pIndex) in section.paragraphs"
                :key="`terms-p-${pIndex}`"
                class="terms-p"
              >
                {{ paragraph }}
              </p>
              <ul v-if="section.list && section.list.length" class="terms-list">
                <li
                  v-for="(item, j) in section.list"
                  :key="j"
                  class="terms-list-item"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
          </template>
          <p v-else class="terms-p">{{ termsContentValue }}</p>
        </section>

        <section class="terms-block terms-block--divider">
          <h4 class="terms-block-title">{{ $t("login.privacyPolicy") }}</h4>
          <template v-if="Array.isArray(privacyContentValue)">
            <div
              v-for="(section, i) in parsedPrivacySections"
              :key="`privacy-${i}`"
              class="terms-section"
            >
              <h5 v-if="section.title" class="terms-title">{{ section.title }}</h5>
              <p
                v-for="(paragraph, pIndex) in section.paragraphs"
                :key="`privacy-p-${pIndex}`"
                class="terms-p"
              >
                {{ paragraph }}
              </p>
              <ul v-if="section.list && section.list.length" class="terms-list">
                <li
                  v-for="(item, j) in section.list"
                  :key="j"
                  class="terms-list-item"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
          </template>
          <p v-else class="terms-p">{{ privacyContentValue }}</p>
        </section>
      </div>
      <span slot="footer">
        <el-button @click="showTermsDialog = false">{{ $t("login.close") || $t("login.termsClose") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { authApi } from "@/api/auth";
import { saveAuthData, saveUserInfo } from "@/utils/auth";

export default {
  data() {
    return {
      loading: false,
      agreedTerms: false,
      showTermsDialog: false,
      loginForm: {
        email: "",
        password: "",
        rememberMe: false,
      },
    };
  },
  computed: {
    isMtBrand() {
      return this.$brand && this.$brand.id === 'mt'
    },
    rules() {
      return {
        email: [
          {
            required: true,
            message: this.$t("login.validation.emailRequired"),
            trigger: "blur",
          },
          {
            type: "email",
            message: this.$t("login.validation.emailInvalid"),
            trigger: ["blur", "change"],
          },
        ],
        password: [
          {
            required: true,
            message: this.$t("login.validation.passwordRequired"),
            trigger: "blur",
          },
        ],
      };
    },
    termsContentValue() {
      return this.$t("legal.termsContent");
    },
    privacyContentValue() {
      return this.$t("legal.privacyContent");
    },
    parsedSections() {
      return this.normalizeLegalSections(this.termsContentValue);
    },
    parsedPrivacySections() {
      return this.normalizeLegalSections(this.privacyContentValue);
    },
  },
  methods: {
    normalizeLegalSections(items) {
      if (!Array.isArray(items)) return [];
      return items.map(item => {
        if (!item) return { title: "", paragraphs: [], list: [] };
        if (typeof item === "object") {
          return {
            title: item.title || "",
            paragraphs: Array.isArray(item.paragraphs)
              ? item.paragraphs
              : (item.text ? [item.text] : []),
            list: Array.isArray(item.list) ? item.list : []
          };
        }
        const parts = String(item).split(/\n{2,}/).map(part => part.trim()).filter(Boolean);
        const [title = "", ...paragraphs] = parts;
        return { title, paragraphs, list: [] };
      });
    },
    showTerms() {
      this.showTermsDialog = true;
    },
    handleLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            // 调用真实的登录 API: POST /iam/tenant-api/v1/users/auth/token
            const loginResponse = await authApi.login({
              email: this.loginForm.email,
              password: this.loginForm.password,
            });
            console.log(loginResponse);

            // 验证返回数据中是否有 accessToken
            if (loginResponse && loginResponse.accessToken) {
              console.log(loginResponse.accessToken);

              // 保存认证信息（accessToken, tokenType, expiresIn, refreshToken）
              saveAuthData(loginResponse, this.loginForm.rememberMe);

              // 解析 JWT 并打印 login_user_key
              try {
                const payload = this.decodeJwtPayload(
                  loginResponse.accessToken
                );
                console.log(payload);

                // if (payload && Object.prototype.hasOwnProperty.call(payload, 'login_user_key')) {
                //   console.log('login_user_key:', payload.login_user_key);
                // } else {
                //   console.warn('login_user_key not found in token payload:', payload);
                // }

                // 保存 userInfo 到缓存
                const userInfo = {
                  sub: payload.sub,
                  accountId: payload.accountId,
                  userId: payload.userId,
                  email: payload.email,
                  iat: payload.iat,
                  exp: payload.exp,
                };
                saveUserInfo(userInfo, this.loginForm.rememberMe);
                console.log(payload);
              } catch (e) {
                console.error("Failed to decode accessToken payload:", e);
              }

              console.log("Login successful:", loginResponse);
              this.$message.success(this.$t("login.loginSuccess"));

              // 重定向到控制中心或首页
              const { redirect, restoreBuyDraft } = this.$route.query;
              const redirectPath = Array.isArray(redirect) ? redirect[0] : redirect;
              this.$router.push({
                path: redirectPath || "/Home",
                query: restoreBuyDraft ? { restoreBuyDraft } : {}
              });
            } else {
              // 返回数据中没有 accessToken，登录失败
              console.error(
                "Login failed: No accessToken in response",
                loginResponse.data
              );
              this.$message.error(this.$t("login.loginFailed"));
            }
          } catch (error) {
            console.error("登录失败:", error);
            if (error.response?.status === 401) {
              this.$message.error(this.$t("login.incorrectCredentials"));
            } else if (error.response?.status === 404) {
              this.$message.error(this.$t("login.userNotFound"));
            } else {
              this.$message.error(
                error.response?.data?.message || this.$t("login.loginFailed")
              );
            }
          } finally {
            this.loading = false;
          }
        } else {
          this.$message.error(this.$t("login.formInvalid"));
          return false;
        }
      });
    },
    goBack() {
      this.$router.go(-1);
    },
    goRegister() {
      this.$router.push("/register");
    },
    goResetPassword() {
      this.$router.push("/reset-password");
    },
    decodeJwtPayload(token) {
      try {
        if (!token || typeof token !== "string") return null;
        const parts = token.split(".");
        if (parts.length < 2) return null;
        const payloadB64Url = parts[1];
        const jsonString = this.base64UrlDecode(payloadB64Url);
        return JSON.parse(jsonString);
      } catch (e) {
        console.error("decodeJwtPayload error:", e);
        return null;
      }
    },
    base64UrlDecode(input) {
      try {
        if (!input) return "";
        let base64 = input.replace(/-/g, "+").replace(/_/g, "/");
        const pad = base64.length % 4;
        if (pad) base64 += "=".repeat(4 - pad);
        const binary = atob(base64);
        // Convert binary string to UTF-8
        const utf8 = decodeURIComponent(
          binary
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        return utf8;
      } catch (e) {
        console.error("base64UrlDecode error:", e);
        return "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

// ============ 全局变量 ============
$primary-color: #667eea;
$primary-dark: #764ba2;
$text-dark: #2d3748;
$text-light: #718096;
$border-light: #e2e8f0;
$bg-light: #f7fafc;
$error-color: #f56565;
$success-color: #48bb78;
$transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

// ============ Desktop (>= 1200px) ============
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow-x: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.05"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')
      repeat;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.login-container {
  width: 100%;
  max-width: 550px;
}

.login-card {
  background: #ffffff;
  border-radius: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 3.5rem 3.5rem;
  position: relative;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
}

.back-button {
  position: absolute;
  top: -0.5rem;
  left: -0.5rem;
  width: 40px;
  height: 40px;
  background: #f7fafc;
  border: none;
  border-radius: 0.75rem;
  font-size: 1.3rem;
  color: $text-light;
  cursor: pointer;
  transition: $transition;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: $primary-color;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
}

.login-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin: 0 auto 1.5rem;
  display: block;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.login-logo--mt {
  animation: spin3D 3s linear infinite;
  filter: drop-shadow(0 0 12px rgba(102, 126, 234, .6))
          drop-shadow(0 0 30px rgba(118, 75, 162, .4));
}

@keyframes spin3D {
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
}

.login-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: $text-dark;
  margin: 0 0 0.5rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.login-subtitle {
  font-size: 1.1rem;
  color: $text-light;
  margin: 0;
  line-height: 1.6;
  font-weight: 400;
}

// ============ Form Styles ============
.login-form {
  ::v-deep .el-form-item {
    margin-bottom: 2rem;

    &__label {
      font-weight: 700;
      color: $text-dark;
      padding-bottom: 0.75rem;
      line-height: 1.3;
      font-size: 1rem;
      letter-spacing: 0.01em;
    }
  }

  ::v-deep .el-input {
    position: relative;

    &.is-disabled .el-input__inner {
      background-color: #edf2f7;
      cursor: not-allowed;
      color: $text-light;
    }
  }

  ::v-deep .el-input__inner {
    border-radius: 1rem;
    border: 2px solid $border-light;
    height: 60px;
    background-color: #fafbfc;
    transition: $transition;
    padding-left: 52px;
    padding-right: 52px;
    font-size: 1.1rem;
    font-weight: 500;
    color: $text-dark;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", Arial, sans-serif;

    &::placeholder {
      color: #a0aec0;
      font-weight: 400;
      opacity: 1;
    }

    &:hover {
      border-color: #cbd5e0;
      background-color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &:focus {
      border-color: $primary-color;
      background-color: #fff;
      box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15),
        0 4px 16px rgba(102, 126, 234, 0.2);
      outline: none;
    }
  }

  // 错误状态
  ::v-deep .el-form-item.is-error {
    .el-input__inner {
      border-color: $error-color;
      background-color: #fff5f5;

      &:focus {
        box-shadow: 0 0 0 4px rgba(245, 101, 101, 0.15),
          0 4px 16px rgba(245, 101, 101, 0.2);
      }
    }

    .el-form-item__error {
      color: $error-color;
      font-size: 0.9rem;
      margin-top: 0.5rem;
      font-weight: 500;
    }
  }

  // 成功状态
  ::v-deep .el-form-item.is-success {
    .el-input__inner {
      border-color: $success-color;
    }
  }

  // 前缀图标
  ::v-deep .el-input__prefix {
    left: 16px;
    font-size: 1.4rem;
    color: #a0aec0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: $transition;
    width: 20px;
    height: 100%;
    top: 0;
  }

  ::v-deep .el-input.is-focus .el-input__prefix {
    color: $primary-color;
    transform: scale(1.1);
  }

  // 后缀图标（密码显示按钮）
  ::v-deep .el-input__suffix {
    right: 16px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 100%;
    top: 0;

    .el-input__suffix-inner {
      display: flex;
      align-items: center;
      height: 100%;
    }

    .el-icon-view,
    .el-icon-close {
      font-size: 1.3rem;
      color: #a0aec0;
      cursor: pointer;
      transition: $transition;
      padding: 0.4rem;
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;

      &:hover {
        color: $primary-color;
        background-color: rgba(102, 126, 234, 0.1);
        transform: scale(1.15);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  // 密码输入特殊样式
  ::v-deep .el-input--password .el-input__inner {
    letter-spacing: 0.2em;
    font-family: "Courier New", monospace;
  }
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;

  ::v-deep .el-checkbox__label {
    color: $text-light;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .el-link {
    font-weight: 600;
    font-size: 0.95rem;
  }
}

.login-btn {
  width: 100%;
  border-radius: 1rem;
  padding: 16px 0;
  font-size: 1.15rem;
  font-weight: 700;
  transition: $transition;
  border: none;
  background: linear-gradient(135deg, $primary-color 0%, $primary-dark 100%);
  color: white;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.agreement-section {
  margin-bottom: 1.5rem;

  ::v-deep .el-checkbox__label {
    font-size: 13px;
    color: #475569;
    white-space: normal;
    line-height: 1.6;
  }
}

.terms-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.terms-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: $primary-color;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: inherit;
  transition: all 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    color: $primary-dark;
    background: rgba(102, 126, 234, 0.08);
  }

  &:active {
    transform: scale(0.97);
  }
}

.terms-divider {
  color: #cbd5e1;
  user-select: none;
}

::v-deep .terms-dialog-wrapper .el-dialog__body {
  padding-top: 0;
}

::v-deep .terms-dialog-wrapper {
  margin: 0 auto !important;
  top: 50%;
  transform: translateY(-50%);
}

.terms-block {
  margin-bottom: 32px;
}

.terms-block--divider {
  padding-top: 28px;
  border-top: 1px solid #e2e8f0;
}

.terms-block-title {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px;
}

.terms-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.terms-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.terms-title {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 14px;
  line-height: 1.4;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

// Keep Element UI input icons anchored to the input box across every breakpoint.
.login-form {
  ::v-deep .el-input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  ::v-deep .el-input__inner {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding-left: 48px !important;
    padding-right: 48px !important;
  }

  ::v-deep .el-input__prefix,
  ::v-deep .el-input__suffix {
    position: absolute;
    top: 0 !important;
    bottom: 0 !important;
    height: auto !important;
    margin: 0;
    display: flex !important;
    align-items: center;
    z-index: 2;
    transform: none;
  }

  ::v-deep .el-input__prefix {
    left: 14px !important;
    width: 20px;
    justify-content: center;
    pointer-events: none;
  }

  ::v-deep .el-input__prefix i {
    display: block;
    line-height: 1;
  }

  ::v-deep .el-input__suffix {
    right: 14px !important;
    justify-content: flex-end;
  }

  ::v-deep .el-input__suffix-inner {
    height: auto !important;
    display: flex;
    align-items: center;
  }

  ::v-deep .el-input__suffix .el-input__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    width: 32px;
    line-height: 1;
    margin: 0;
  }
}

@media screen and (max-width: 479px) {
  .login-page {
    min-height: 100dvh;
    padding: 12px;
  }

  .login-card {
    width: 100%;
    padding: 28px 20px;
  }

  .login-form {
    ::v-deep .el-input__inner {
      height: 50px;
      padding-left: 44px !important;
      padding-right: 44px !important;
    }

    ::v-deep .el-input__prefix {
      left: 12px !important;
    }

    ::v-deep .el-input__suffix {
      right: 10px !important;
    }
  }
}

.terms-p {
  margin: 0 0 14px;
  line-height: 1.2;
  font-size: 14.5px;
  color: #475569;
  text-align: justify;
  word-break: break-word;
}

.terms-list {
  list-style: none;
  padding: 0;
  margin: 10px 0;

  .terms-list-item {
    position: relative;
    padding-left: 20px;
    margin-bottom: 10px;
    line-height: 1;
    font-size: 14.5px;
    color: #475569;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.5em;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: $primary-color;
      transform: translateY(-50%);
    }
  }
}

.register-prompt {
  text-align: center;
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 2px solid $border-light;
  color: $text-light;
  font-size: 1rem;

  span {
    display: inline;
  }

  .register-link {
    font-weight: 700;
    margin-left: 0.5rem;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

// ============ Tablet (768px - 1199px) ============
@media screen and (max-width: 1199px) and (min-width: 768px) {
  .login-card {
    padding: 3rem 2.5rem;
  }

  .login-header {
    margin-bottom: 2.5rem;
  }

  .login-logo {
    width: 75px;
    height: 75px;
    margin-bottom: 1.25rem;
  }

  .login-title {
    font-size: 2rem;
  }

  .login-subtitle {
    font-size: 1rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 1.75rem;

      &__label {
        font-size: 0.95rem;
        padding-bottom: 0.6rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 56px;
      font-size: 1.05rem;
      padding-left: 48px;
      padding-right: 48px;
      border-radius: 0.95rem;
    }

    ::v-deep .el-input__prefix {
      left: 15px;
      font-size: 1.35rem;
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      right: 15px;
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        font-size: 1.25rem;
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 1.75rem;

    ::v-deep .el-checkbox__label {
      font-size: 0.9rem;
    }

    .el-link {
      font-size: 0.9rem;
    }
  }

  .login-btn {
    padding: 15px 0;
    font-size: 1.1rem;
    border-radius: 0.95rem;
  }

  .register-prompt {
    margin-top: 2rem;
    padding-top: 1.5rem;
    font-size: 0.95rem;
  }
}

// ============ Large Mobile (576px - 767px) ============
@media screen and (max-width: 767px) and (min-width: 576px) {
  .login-page {
    padding: 1.5rem;
  }

  .login-container {
    max-width: 100%;
  }

  .login-card {
    padding: 2.5rem 2rem;
    border-radius: 1.25rem;
  }

  .login-header {
    margin-bottom: 2rem;
  }

  .back-button {
    width: 36px;
    height: 36px;
    font-size: 1.2rem;
    left: -0.5rem;
    top: -0.5rem;
  }

  .login-logo {
    width: 65px;
    height: 65px;
    margin-bottom: 1rem;
  }

  .login-title {
    font-size: 1.75rem;
  }

  .login-subtitle {
    font-size: 0.95rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 1.5rem;

      &__label {
        font-size: 0.9rem;
        padding-bottom: 0.5rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 54px;
      font-size: 1rem;
      padding-left: 44px;
      padding-right: 44px;
      border-radius: 0.9rem;
    }

    ::v-deep .el-input__prefix {
      left: 14px;
      font-size: 1.25rem;
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      right: 14px;
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        font-size: 1.15rem;
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 1.5rem;
    gap: 0.75rem;

    ::v-deep .el-checkbox__label {
      font-size: 0.85rem;
    }

    .el-link {
      font-size: 0.85rem;
      display: block;
    }
  }

  .login-btn {
    padding: 14px 0;
    font-size: 1.05rem;
    border-radius: 0.9rem;
  }

  .register-prompt {
    margin-top: 1.75rem;
    padding-top: 1.25rem;
    font-size: 0.9rem;
  }
}

// ============ Mobile (480px - 575px) ============
@media screen and (max-width: 575px) and (min-width: 480px) {
  .login-page {
    padding: 1rem;
    padding-top: 1.5rem;
  }

  .login-container {
    max-width: 100%;
  }

  .login-card {
    padding: 2rem 1.5rem;
    border-radius: 1.1rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  .login-header {
    margin-bottom: 1.75rem;
  }

  .back-button {
    width: 34px;
    height: 34px;
    font-size: 1.1rem;
  }

  .login-logo {
    width: 60px;
    height: 60px;
    margin-bottom: 0.875rem;
  }

  .login-title {
    font-size: 1.6rem;
  }

  .login-subtitle {
    font-size: 0.9rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 1.25rem;

      &__label {
        font-size: 0.88rem;
        padding-bottom: 0.4rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 50px;
      font-size: 0.95rem;
      padding-left: 40px;
      padding-right: 40px;
      border-radius: 0.85rem;
    }

    ::v-deep .el-input__prefix {
      left: 12px;
      font-size: 1.15rem;
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      right: 12px;
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        font-size: 1.05rem;
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 1.25rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;

    ::v-deep .el-checkbox {
      width: 100%;
    }

    ::v-deep .el-checkbox__label {
      font-size: 0.85rem;
    }

    .el-link {
      font-size: 0.85rem;
      display: block;
    }
  }

  .login-btn {
    padding: 13px 0;
    font-size: 1rem;
    border-radius: 0.85rem;
  }

  .register-prompt {
    margin-top: 1.5rem;
    padding-top: 1rem;
    font-size: 0.85rem;
  }
}

// ============ Small Mobile (< 480px) ============
@media screen and (max-width: 479px) {
  .login-page {
    padding: 0.75rem;
    padding-top: 1.25rem;
  }

  .login-container {
    max-width: 100%;
  }

  .login-card {
    padding: 1.75rem 1.25rem;
    border-radius: 1rem;
  }

  .login-header {
    margin-bottom: 1.5rem;
  }

  .back-button {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    top: -0.25rem;
    left: -0.25rem;
  }

  .login-logo {
    width: 55px;
    height: 55px;
    margin-bottom: 0.75rem;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .login-subtitle {
    font-size: 0.85rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 1.1rem;

      &__label {
        font-size: 0.85rem;
        padding-bottom: 0.35rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 48px;
      font-size: 0.9rem;
      padding-left: 38px;
      padding-right: 38px;
      border-radius: 0.8rem;
    }

    ::v-deep .el-input__prefix {
      left: 11px;
      font-size: 1.1rem;
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      right: 11px;
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        font-size: 1rem;
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 1rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;

    ::v-deep .el-checkbox__label {
      font-size: 0.8rem;
    }

    .el-link {
      font-size: 0.8rem;
    }
  }

  .login-btn {
    padding: 12px 0;
    font-size: 0.95rem;
    border-radius: 0.8rem;
  }

  .register-prompt {
    margin-top: 1.25rem;
    padding-top: 0.875rem;
    font-size: 0.8rem;

    span,
    .register-link {
      font-size: 0.8rem;
    }
  }
}

// ============ Extra Small Mobile (< 360px) ============
@media screen and (max-width: 359px) {
  .login-card {
    padding: 1.5rem 1rem;
    border-radius: 0.9rem;
  }

  .login-header {
    margin-bottom: 1.25rem;
  }

  .login-logo {
    width: 50px;
    height: 50px;
    margin-bottom: 0.5rem;
  }

  .login-title {
    font-size: 1.4rem;
    margin-bottom: 0.25rem;
  }

  .login-subtitle {
    font-size: 0.8rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 1rem;

      &__label {
        font-size: 0.8rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 46px;
      font-size: 0.85rem;
      padding-left: 36px;
      padding-right: 36px;
    }

    ::v-deep .el-input__prefix {
      font-size: 1rem;
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 0.875rem;

    ::v-deep .el-checkbox__label {
      font-size: 0.75rem;
    }
  }

  .login-btn {
    padding: 11px 0;
    font-size: 0.9rem;
  }

  .register-prompt {
    margin-top: 1rem;
    padding-top: 0.75rem;
    font-size: 0.75rem;
  }
}

// ============ Landscape Mode ============
@media screen and (max-height: 600px) and (orientation: landscape) {
  .login-page {
    padding: 1rem;
    min-height: auto;
  }

  .login-container {
    max-width: 600px;
  }

  .login-card {
    padding: 1.5rem;
  }

  .login-header {
    margin-bottom: 1rem;
  }

  .login-logo {
    width: 50px;
    height: 50px;
    margin-bottom: 0.5rem;
  }

  .login-title {
    font-size: 1.4rem;
  }

  .login-subtitle {
    font-size: 0.8rem;
  }

  .login-form {
    ::v-deep .el-form-item {
      margin-bottom: 0.9rem;

      &__label {
        font-size: 0.85rem;
        padding-bottom: 0.3rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 44px;
      font-size: 0.9rem;
    }

    ::v-deep .el-input__prefix {
      height: 100%;
      top: 0;
    }

    ::v-deep .el-input__suffix {
      height: 100%;
      top: 0;

      .el-input__suffix-inner {
        height: 100%;
      }

      .el-icon-view,
      .el-icon-close {
        height: 100%;
      }
    }
  }

  .form-options {
    margin-bottom: 0.9rem;
    flex-direction: row;
  }

  .login-btn {
    padding: 10px 0;
    font-size: 0.95rem;
  }

  .register-prompt {
    margin-top: 1rem;
    padding-top: 0.75rem;
    font-size: 0.8rem;
  }
}

// ============ Print ============
@media print {
  .login-page {
    background-color: white;

    &::before {
      display: none;
    }
  }

  .back-button {
    display: none;
  }

  .login-card {
    box-shadow: none;
    border: 1px solid $border-light;
  }

  .login-btn {
    box-shadow: none;
  }
}

// ============ Terms Dialog (matching OrderConfirm Step 4) ============
.terms-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    &:hover { background: #94a3b8; }
  }
}



.terms-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 14px;
  line-height: 1.4;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}




.terms-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.terms-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: $primary-color;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: inherit;
  transition: all 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    color: $primary-dark;
    background: rgba(10, 127, 215, 0.06);
  }

  &:active {
    transform: scale(0.97);
  }
}

.terms-divider {
  color: #cbd5e1;
  user-select: none;
}

/* 标签页 */
.terms-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 20px;
}

.terms-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;

  i { font-size: 16px; }

  &:hover {
    color: $primary-color;
  }

  &.is-active {
    color: $primary-color;
    border-bottom-color: $primary-color;
  }
}

/* 弹窗内间距调整 */
::v-deep .terms-dialog-wrapper .el-dialog__body {
  padding-top: 0;
}

::v-deep .terms-dialog-wrapper {
  margin: 0 auto !important;
  top: 50%;
  transform: translateY(-50%);
}

.terms-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    &:hover { background: #94a3b8; }
  }
}



.terms-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 14px;
  line-height: 1.4;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}





</style>
